import { create } from 'zustand';
import { RecordModel } from 'pocketbase';
import { getPb } from './pocketbase';
import { captureEvent } from './config';
import {
  clearCachedPilot,
  isNetworkFailure,
  readCachedPilot,
  writeCachedPilot,
} from './lib/auth-cache';
import { Pilot } from './types';

interface AuthStore {
  isAuthenticated: boolean;
  user: RecordModel | null;
  pilot: Pilot | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    name: string,
    email: string,
    password: string,
    passwordConfirm: string,
    cpf: string,
  ) => Promise<void>;
  logout: () => void;
  setPilot: (pilot: Pilot | null) => void;
  updateUser: (user: RecordModel) => void;
  initialize: () => Promise<void>;
}

export type AuthSnapshot = {
  isAuthenticated: boolean;
  user: RecordModel | null;
  pilot: Pilot | null;
};

/**
 * App-specific side effects injected by each host (web injects Sentry user
 * context, PostHog identify, auth cookie sync and Next server actions; the
 * native SPA leaves most of these unset).
 */
export type AuthEffects = {
  /** Resolve a pilot for a user without a `piloto` relation (web calls the
   *  admin-backed /api/pilots/link route). When absent, no pilot is linked
   *  or created — the user gets one via the web flow. */
  linkPilot?: (args: {
    user: RecordModel;
    email: string;
    cpf?: string;
  }) => Promise<Pilot | null>;
  /** Create the user account (web uses a Next server action). Absent →
   *  register() throws. */
  registerUser?: (
    name: string,
    email: string,
    cpf: string,
    password: string,
    passwordConfirm: string,
  ) => Promise<void>;
  /** Fire-and-forget hook after a successful login (web: IP tracking). */
  afterLogin?: (userId: string) => void;
  /** Keep host session artifacts (cookies) in sync with pb.authStore. */
  syncSession?: () => void;
  /** Auth identity changed — set/clear error-tracking + analytics user. */
  onAuthChanged?: (snapshot: AuthSnapshot) => void;
};

let effects: AuthEffects = {};

export function setAuthEffects(next: AuthEffects): void {
  effects = next;
}

export const mapRecordToPilot = (record: RecordModel): Pilot => ({
  id: record.id,
  nome: record.nome,
  email: record.email,
  dataNascimento: record.dataNascimento,
  cnh: record.cnh,
  categoria: record.categoria,
  cpf: record.cpf,
  phone: record.phone,
  instagram: record.instagram,
  cupom_promoter: record.cupom_promoter,
  created: record.created,
  updated: record.updated,
  collectionId: record.collectionId,
  collectionName: record.collectionName,
  expand: record.expand,
  nome_completo: record.nome_completo,
  liveName: record.liveName,
});

const fetchAndLinkPilot = async (
  user: RecordModel,
  email?: string,
  cpf?: string,
) => {
  const pb = getPb();
  const normalizedEmail = email?.toLowerCase();

  if (user.piloto) {
    const pilotRecord = await pb.collection('Pilotos').getOne(user.piloto);
    return mapRecordToPilot(pilotRecord);
  }
  if (normalizedEmail && effects.linkPilot) {
    return effects.linkPilot({ user, email: normalizedEmail, cpf });
  }
  return null;
};

const notifyAuthChanged = (snapshot: AuthSnapshot) => {
  try {
    effects.onAuthChanged?.(snapshot);
  } catch (error) {
    console.error('Error in onAuthChanged effect:', error);
  }
};

const capture = captureEvent;

export const useAuthStore = create<AuthStore>((set) => ({
  isAuthenticated: false,
  user: null,
  pilot: null,
  isLoading: true,
  initialize: async () => {
    const pb = getPb();

    const signOut = () => {
      pb.authStore.clear();
      clearCachedPilot();
      set({
        isAuthenticated: false,
        user: null,
        pilot: null,
        isLoading: false,
      });
      notifyAuthChanged({ isAuthenticated: false, user: null, pilot: null });
      effects.syncSession?.();
    };

    if (!pb.authStore.isValid || !pb.authStore.model) {
      signOut();
      return;
    }

    // authStore.model is loosely typed by the SDK, but it is the users record.
    const currentUser = pb.authStore.model as RecordModel;

    try {
      // Refresh the user and its pilot from the server.
      const user = await pb.collection('users').getOne(currentUser.id);
      let pilot: Pilot | null = null;

      if (user.piloto) {
        const pilotRecord = await pb.collection('Pilotos').getOne(user.piloto);
        pilot = mapRecordToPilot(pilotRecord);
      }

      writeCachedPilot(user.id, pilot);
      set({ isAuthenticated: true, user, pilot, isLoading: false });
      notifyAuthChanged({ isAuthenticated: true, user, pilot });
      effects.syncSession?.();
    } catch (error) {
      // A token the server rejected is really dead — sign out. A network
      // failure is not: dropping the session offline would log the pilot out
      // on a bad connection and take every cached screen down with it.
      if (!isNetworkFailure(error)) {
        console.error('Auth session rejected by the server:', error);
        signOut();
        return;
      }

      console.warn('Offline auth init — keeping the stored session:', error);
      const pilot = readCachedPilot(currentUser.id);
      set({
        isAuthenticated: true,
        user: currentUser,
        pilot,
        isLoading: false,
      });
      notifyAuthChanged({ isAuthenticated: true, user: currentUser, pilot });
      effects.syncSession?.();
    }
  },
  login: async (email: string, password: string) => {
    const pb = getPb();
    // Normalize email to lowercase before authenticating
    const normalizedEmail = email.toLowerCase();
    const authData = await pb
      .collection('users')
      .authWithPassword(normalizedEmail, password);
    const user = authData.record;
    let pilot = null;

    try {
      pilot = await fetchAndLinkPilot(user, normalizedEmail);
    } catch (error) {
      console.error('Error fetching/linking pilot data:', error);
    }

    try {
      effects.afterLogin?.(user.id);
    } catch (error) {
      console.error('Error in afterLogin effect:', error);
    }

    writeCachedPilot(user.id, pilot);
    set({ isAuthenticated: true, user: user, pilot: pilot });
    notifyAuthChanged({ isAuthenticated: true, user, pilot });
    capture('user_logged_in', { auth_method: 'password' });
    effects.syncSession?.();
  },
  register: async (
    name: string,
    email: string,
    password: string,
    passwordConfirm: string,
    cpf: string,
  ) => {
    if (!effects.registerUser) {
      throw new Error(
        'Registro não disponível no aplicativo. Crie sua conta pelo site.',
      );
    }
    await effects.registerUser(name, email, cpf, password, passwordConfirm);

    const pb = getPb();
    // Authenticate to get the session token (registration does not return one)
    const normalizedEmail = email.toLowerCase();
    const authData = await pb
      .collection('users')
      .authWithPassword(normalizedEmail, password);
    const user = authData.record;

    // Fetch pilot data (it should have been linked/created during registration)
    let pilot = null;
    if (user.piloto) {
      const pilotRecord = await pb.collection('Pilotos').getOne(user.piloto);
      pilot = mapRecordToPilot(pilotRecord);
    }

    writeCachedPilot(user.id, pilot);
    set({ isAuthenticated: true, user: user, pilot: pilot });
    notifyAuthChanged({ isAuthenticated: true, user, pilot });
    capture('user_registered', { auth_method: 'password' });
    effects.syncSession?.();
  },
  logout: () => {
    const pb = getPb();
    pb.authStore.clear();
    clearCachedPilot();
    set({ isAuthenticated: false, user: null, pilot: null });
    notifyAuthChanged({ isAuthenticated: false, user: null, pilot: null });
    capture('user_logged_out');
    effects.syncSession?.();
  },
  setPilot: (pilot: Pilot | null) => {
    const userId = useAuthStore.getState().user?.id;
    if (userId) writeCachedPilot(userId, pilot);
    set({ pilot });
  },
  updateUser: (user: RecordModel) => set({ user }),
}));

// Initialize auth state
export const initializeAuthStore = async () => {
  await useAuthStore.getState().initialize();

  // Expose auth store globally for Sentry access
  if (typeof window !== 'undefined') {
    (window as unknown as { __AUTH_MODULE__?: unknown }).__AUTH_MODULE__ = {
      useAuthStore,
    } as unknown;
  }
};
